/**
 * @file useTheme.ts
 * @description Hook personalizado para gestionar modo de color (light/dark)
 * Sincroniza con cookies y aplica cambios al DOM en tiempo real
 * Theme fijo: default-fullwidth
 * @author XiaVan Team
 */

import { useEffect, useState, useCallback } from 'react'
import {
  getMode,
  setMode,
  initializeModeFromSystem,
} from '@/lib/cookies'

type Mode = 'light' | 'dark'

interface UseThemeReturn {
  mode: Mode
  setMode: (mode: Mode) => void
  toggleMode: () => void
}

/**
 * Hook para gestionar modo de color (light/dark)
 * Theme fijo: default-fullwidth
 *
 * Features:
 * - Lee modo de cookies al cargar
 * - Detecta preferencia del sistema en primera visita
 * - Aplica cambios al atributo data-mode del <html>
 * - Persiste cambios en cookies
 * - Sincronización entre pestañas (via storage event)
 *
 * @example
 * const { mode, toggleMode } = useTheme()
 *
 * return (
 *   <button onClick={toggleMode}>
 *     {mode === 'light' ? '🌙' : '☀️'}
 *   </button>
 * )
 */
export function useTheme(): UseThemeReturn {
  const [mode, setModeState] = useState<Mode>('light')
  const [mounted, setMounted] = useState(false)

  /**
   * Aplicar modo a los atributos del HTML
   */
  const applyModeToDOM = useCallback(
    (newMode: Mode) => {
      const html = document.documentElement

      // Theme fijo
      html.setAttribute('data-theme', 'default-fullwidth')
      html.setAttribute('data-mode', newMode)

      // Clases helper para compatibilidad con Tailwind dark:
      if (newMode === 'dark') {
        html.classList.add('dark')
      } else {
        html.classList.remove('dark')
      }

      // Clase helper para el tema
      html.classList.remove(
        'theme-default-fullwidth-light',
        'theme-default-fullwidth-dark'
      )
      html.classList.add(`theme-default-fullwidth-${newMode}`)
    },
    []
  )

  // Inicializar desde cookies al montar componente
  useEffect(() => {
    const savedMode = getMode()

    // Si es primera visita (no hay cookie de modo), usar preferencia del sistema
    if (!savedMode) {
      const systemMode = initializeModeFromSystem()
      setModeState(systemMode)
      applyModeToDOM(systemMode)
    } else {
      setModeState(savedMode)
      applyModeToDOM(savedMode)
    }

    setMounted(true)
  }, [applyModeToDOM])

  /**
   * Cambiar el modo
   */
  const handleSetMode = useCallback(
    (newMode: Mode) => {
      if (!mounted) return

      setModeState(newMode)
      setMode(newMode)
      applyModeToDOM(newMode)
    },
    [mounted, applyModeToDOM]
  )

  /**
   * Toggle entre light y dark
   */
  const toggleMode = useCallback(() => {
    const newMode = mode === 'light' ? 'dark' : 'light'
    handleSetMode(newMode)
  }, [mode, handleSetMode])

  // Listener para sincronización entre pestañas
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'xiavan_mode' && e.newValue) {
        const newMode = e.newValue as Mode
        setModeState(newMode)
        applyModeToDOM(newMode)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [applyModeToDOM])

  return {
    mode,
    setMode: handleSetMode,
    toggleMode,
  }
}
